/**
 * FhirClinicalPanel — Clinical summary for a subject (DRR-123, DRR-114)
 *
 * Healthcare-specific component in the FHIR subgraph package (DRR-073).
 * Shows FHIR conditions, medications, and observations for a subject.
 * Gated by clinical:read scope on the backend (DRR-123 information barrier).
 *
 * Data tier: T2 (operational, 1-year retention per DRR-114).
 * No duration variance or staffing ratios — those are T3 QAPI-only.
 *
 * DUMB FRONTEND: Parses fhirJson from FhirStoredResourceType, displays
 * fields dynamically. No hardcoded clinical logic.
 */

import { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Skeleton,
  Alert,
  Table,
  Tabs,
} from '@mantine/core';
import {
  IconStethoscope,
  IconPill,
  IconHeartRateMonitor,
  IconAlertCircle,
  IconChartLine,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { ObservationChart, type ObservationDataPoint } from './ObservationChart';

// ============================================================================
// GraphQL queries — inline to avoid portal @/generated dependency (DRR-073)
// ============================================================================

const LIST_FHIR_CONDITIONS = gql`
  query AdminListFhirConditionsPanel($tenantNum: Int!, $limit: Int = 100) {
    adminListFhirConditions(tenantNum: $tenantNum, limit: $limit) {
      resourceId
      fhirJson
      isLatest
    }
  }
`;

const LIST_FHIR_MEDICATION_REQUESTS = gql`
  query AdminListFhirMedicationRequestsPanel($tenantNum: Int!, $limit: Int = 100) {
    adminListFhirMedicationRequests(tenantNum: $tenantNum, limit: $limit) {
      resourceId
      fhirJson
      isLatest
    }
  }
`;

const LIST_FHIR_OBSERVATIONS = gql`
  query AdminListFhirObservationsPanel($tenantNum: Int!, $limit: Int = 100) {
    adminListFhirObservations(tenantNum: $tenantNum, limit: $limit) {
      resourceId
      fhirJson
      isLatest
    }
  }
`;

const LIST_FHIR_MEDICATION_ADMINISTRATIONS = gql`
  query AdminListFhirMedicationAdministrationsPanel($tenantNum: Int!, $limit: Int = 100) {
    adminListFhirMedicationAdministrations(tenantNum: $tenantNum, limit: $limit) {
      resourceId
      fhirJson
      isLatest
    }
  }
`;

// ============================================================================
// Types — parsed from fhirJson
// ============================================================================

interface ParsedCondition {
  id: string;
  code: string;
  display: string;
  clinicalStatus: string;
  verificationStatus: string;
  onsetDate?: string;
}

interface ParsedMedication {
  id: string;
  display: string;
  status: string;
  intent: string;
  dosage?: string;
  frequency?: string;
}

interface ParsedObservation {
  id: string;
  code: string;
  display: string;
  value?: string;
  unit?: string;
  status: string;
  effectiveDate?: string;
  interpretation?: string;
}

interface ParsedMedicationAdministration {
  id: string;
  medicationDisplay: string;
  status: string;
  effectiveDateTime?: string;
  performerName?: string;
  dosage?: string;
  route?: string;
  wasNotGiven: boolean;
  reasonNotGiven?: string;
}

// ============================================================================
// FHIR R4 JSON parsers
// ============================================================================

function getFirstCoding(concept: unknown): { code?: string; display?: string } | undefined {
  if (!concept || typeof concept !== 'object') return undefined;
  const cc = concept as Record<string, unknown>;
  const codings = cc.coding as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(codings) && codings.length > 0) {
    const first = codings[0];
    if (!first) return undefined;
    return { code: first.code as string, display: first.display as string };
  }
  if (typeof cc.text === 'string') return { display: cc.text };
  return undefined;
}

function parseCondition(resourceId: string, json: string): ParsedCondition | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;
    const coding = getFirstCoding(f.code);
    const clinicalCoding = getFirstCoding(f.clinicalStatus);
    const verifCoding = getFirstCoding(f.verificationStatus);
    return {
      id: resourceId,
      code: coding?.code ?? '',
      display: coding?.display ?? 'Unknown Condition',
      clinicalStatus: clinicalCoding?.code ?? 'unknown',
      verificationStatus: verifCoding?.code ?? 'unknown',
      onsetDate: typeof f.onsetDateTime === 'string' ? f.onsetDateTime.slice(0, 10) : undefined,
    };
  } catch {
    return null;
  }
}

function parseMedication(resourceId: string, json: string): ParsedMedication | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;
    const coding = getFirstCoding(f.medicationCodeableConcept);
    const dosageArr = f.dosageInstruction as Array<Record<string, unknown>> | undefined;
    let dosage: string | undefined;
    let frequency: string | undefined;
    if (Array.isArray(dosageArr) && dosageArr.length > 0) {
      const d = dosageArr[0] as Record<string, unknown> | undefined;
      if (d?.text && typeof d.text === 'string') dosage = d.text;
      const timing = d?.timing as Record<string, unknown> | undefined;
      if (timing?.code) {
        const tc = getFirstCoding(timing.code);
        frequency = tc?.display ?? tc?.code;
      }
    }
    return {
      id: resourceId,
      display: coding?.display ?? 'Unknown Medication',
      status: typeof f.status === 'string' ? f.status : 'unknown',
      intent: typeof f.intent === 'string' ? f.intent : 'order',
      dosage,
      frequency,
    };
  } catch {
    return null;
  }
}

function parseObservation(resourceId: string, json: string): ParsedObservation | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;
    const coding = getFirstCoding(f.code);
    const vq = f.valueQuantity as Record<string, unknown> | undefined;
    let value: string | undefined;
    let unit: string | undefined;
    if (vq) {
      value = vq.value != null ? String(vq.value) : undefined;
      unit = typeof vq.unit === 'string' ? vq.unit : undefined;
    } else if (typeof f.valueString === 'string') {
      value = f.valueString;
    }
    const interpArr = f.interpretation as Array<Record<string, unknown>> | undefined;
    const interpCoding = Array.isArray(interpArr) ? getFirstCoding(interpArr[0]) : undefined;

    return {
      id: resourceId,
      code: coding?.code ?? '',
      display: coding?.display ?? 'Unknown Observation',
      value,
      unit,
      status: typeof f.status === 'string' ? f.status : 'unknown',
      effectiveDate: typeof f.effectiveDateTime === 'string' ? f.effectiveDateTime.slice(0, 10) : undefined,
      interpretation: interpCoding?.display ?? interpCoding?.code,
    };
  } catch {
    return null;
  }
}

function parseMedicationAdministration(resourceId: string, json: string): ParsedMedicationAdministration | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;
    const medCoding = getFirstCoding(f.medicationCodeableConcept);
    const performerArr = f.performer as Array<Record<string, unknown>> | undefined;
    let performerName: string | undefined;
    if (Array.isArray(performerArr) && performerArr.length > 0) {
      const actor = performerArr[0]?.actor as Record<string, unknown> | undefined;
      performerName = actor?.display as string | undefined;
    }
    const dosageObj = f.dosage as Record<string, unknown> | undefined;
    let dosage: string | undefined;
    let route: string | undefined;
    if (dosageObj) {
      dosage = dosageObj.text as string | undefined;
      const routeCoding = getFirstCoding(dosageObj.route);
      route = routeCoding?.display ?? routeCoding?.code;
    }
    const statusReasonCoding = getFirstCoding(f.statusReason);
    const status = typeof f.status === 'string' ? f.status : 'unknown';
    return {
      id: resourceId,
      medicationDisplay: medCoding?.display ?? 'Unknown Medication',
      status,
      effectiveDateTime: typeof f.effectiveDateTime === 'string' ? f.effectiveDateTime : undefined,
      performerName,
      dosage,
      route,
      wasNotGiven: status === 'not-done' || status === 'on-hold',
      reasonNotGiven: statusReasonCoding?.display,
    };
  } catch {
    return null;
  }
}

// ============================================================================
// Status badge color helpers — FHIR R4 standard value sets
// ============================================================================

const CLINICAL_STATUS_COLOR: Record<string, string> = {
  active: 'red',
  recurrence: 'orange',
  relapse: 'orange',
  inactive: 'gray',
  remission: 'green',
  resolved: 'green',
};

const MED_STATUS_COLOR: Record<string, string> = {
  active: 'blue',
  completed: 'green',
  'on-hold': 'yellow',
  stopped: 'red',
  cancelled: 'gray',
  draft: 'gray',
};

const INTERP_COLOR: Record<string, string> = {
  N: 'green',
  H: 'red',
  L: 'orange',
  HH: 'red',
  LL: 'orange',
  A: 'red',
};

const MAR_STATUS_COLOR: Record<string, string> = {
  completed: 'green',
  'in-progress': 'blue',
  'not-done': 'red',
  'on-hold': 'yellow',
  stopped: 'red',
  unknown: 'gray',
};

// ============================================================================
// Subject filter — DRY helper for any FHIR resource with subject.reference
// ============================================================================

/**
 * Extract subject reference from FHIR JSON. Works for Condition, MedicationRequest,
 * Observation, Encounter, CarePlan — any resource with `subject.reference`.
 * Returns the patient resource ID (e.g., "17-resident-001") or null.
 *
 * DRY: Other subgraph packages can copy this pattern for subject filtering.
 * The key insight: FHIR resources store subject as `subject.reference: "Patient/{id}"`.
 */
function extractSubjectRef(fhirJson: string): string | null {
  try {
    const f = JSON.parse(fhirJson) as Record<string, unknown>;
    const subject = f.subject as Record<string, unknown> | undefined;
    if (!subject?.reference) return null;
    const ref = subject.reference as string;
    // FHIR reference format: "Patient/{id}" or just the id
    return ref.startsWith('Patient/') ? ref.slice(8) : ref;
  } catch {
    return null;
  }
}

// ============================================================================
// Result type for stored resource queries
// ============================================================================

interface StoredResource {
  resourceId: string;
  fhirJson: string;
  isLatest: boolean;
}

// ============================================================================
// Component
// ============================================================================

export interface FhirClinicalPanelProps {
  /** Subject actor ID to show clinical data for (auth_user_id format, e.g., "17-resident-001") */
  readonly subjectActorId: string;
  /** Tenant numeric ID — from auth context (no hardcoded default) */
  readonly tenantNum: number;
}

export function FhirClinicalPanel({
  subjectActorId,
  tenantNum,
}: FhirClinicalPanelProps) {
  const skip = !subjectActorId || !tenantNum;

  const { data: condData, loading: condLoading, error: condError } = useQuery<{
    adminListFhirConditions: StoredResource[];
  }>(LIST_FHIR_CONDITIONS, {
    variables: { tenantNum, limit: 100 },
    skip,
    fetchPolicy: 'cache-first',
  });

  const { data: medData, loading: medLoading, error: medError } = useQuery<{
    adminListFhirMedicationRequests: StoredResource[];
  }>(LIST_FHIR_MEDICATION_REQUESTS, {
    variables: { tenantNum, limit: 100 },
    skip,
    fetchPolicy: 'cache-first',
  });

  const { data: obsData, loading: obsLoading, error: obsError } = useQuery<{
    adminListFhirObservations: StoredResource[];
  }>(LIST_FHIR_OBSERVATIONS, {
    variables: { tenantNum, limit: 100 },
    skip,
    fetchPolicy: 'cache-first',
  });

  const { data: marData, loading: marLoading, error: marError } = useQuery<{
    adminListFhirMedicationAdministrations: StoredResource[];
  }>(LIST_FHIR_MEDICATION_ADMINISTRATIONS, {
    variables: { tenantNum, limit: 100 },
    skip,
    fetchPolicy: 'cache-first',
  });

  const loading = condLoading || medLoading || obsLoading || marLoading;
  const error = condError ?? medError ?? obsError ?? marError;

  // Filter by subject: only show resources whose subject.reference matches this actor.
  // DRY pattern: filter stored resources by subject reference client-side.
  // When backend adds patientId filter param, this becomes a no-op (data already filtered).
  const matchesSubject = (r: StoredResource): boolean => {
    const ref = extractSubjectRef(r.fhirJson);
    return ref === subjectActorId;
  };

  const conditions = useMemo<ParsedCondition[]>(() => {
    const items = condData?.adminListFhirConditions ?? [];
    return items
      .filter((r) => r.isLatest && matchesSubject(r))
      .map((r) => parseCondition(r.resourceId, r.fhirJson))
      .filter(Boolean) as ParsedCondition[];
  }, [condData, subjectActorId]);

  const medications = useMemo<ParsedMedication[]>(() => {
    const items = medData?.adminListFhirMedicationRequests ?? [];
    return items
      .filter((r) => r.isLatest && matchesSubject(r))
      .map((r) => parseMedication(r.resourceId, r.fhirJson))
      .filter(Boolean) as ParsedMedication[];
  }, [medData, subjectActorId]);

  const observations = useMemo<ParsedObservation[]>(() => {
    const items = obsData?.adminListFhirObservations ?? [];
    return items
      .filter((r) => r.isLatest && matchesSubject(r))
      .map((r) => parseObservation(r.resourceId, r.fhirJson))
      .filter(Boolean) as ParsedObservation[];
  }, [obsData, subjectActorId]);

  const medicationAdministrations = useMemo<ParsedMedicationAdministration[]>(() => {
    const items = marData?.adminListFhirMedicationAdministrations ?? [];
    return items
      .filter((r) => matchesSubject(r))
      .map((r) => parseMedicationAdministration(r.resourceId, r.fhirJson))
      .filter(Boolean) as ParsedMedicationAdministration[];
  }, [marData, subjectActorId]);

  // Group observations by display name for charting (all versions, not just latest).
  // DRY: This pattern works for any FHIR Observation with valueQuantity + effectiveDateTime.
  const observationCharts = useMemo(() => {
    const items = obsData?.adminListFhirObservations ?? [];
    const allObs = items
      .filter((r) => matchesSubject(r))
      .map((r) => parseObservation(r.resourceId, r.fhirJson))
      .filter(Boolean) as ParsedObservation[];

    const grouped = new Map<string, ObservationDataPoint[]>();
    for (const obs of allObs) {
      if (!obs.value || !obs.effectiveDate) continue;
      const numVal = Number.parseFloat(obs.value);
      if (Number.isNaN(numVal)) continue;
      const key = obs.display;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push({
        date: obs.effectiveDate,
        value: numVal,
        unit: obs.unit ?? '',
        status: obs.status,
      });
    }
    // Sort each group by date
    for (const points of grouped.values()) {
      points.sort((a, b) => a.date.localeCompare(b.date));
    }
    return grouped;
  }, [obsData, subjectActorId]);

  if (loading) {
    return (
      <Stack gap="sm">
        <Skeleton height={30} />
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Could not load clinical data. This may require clinical:read scope.
      </Alert>
    );
  }

  return (
    <Tabs defaultValue="conditions" keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="conditions" leftSection={<IconStethoscope size={14} />}>
          Conditions ({conditions.length})
        </Tabs.Tab>
        <Tabs.Tab value="medications" leftSection={<IconPill size={14} />}>
          Medications ({medications.length})
        </Tabs.Tab>
        <Tabs.Tab value="observations" leftSection={<IconHeartRateMonitor size={14} />}>
          Observations ({observations.length})
        </Tabs.Tab>
        <Tabs.Tab value="mar" leftSection={<IconPill size={14} />}>
          MAR ({medicationAdministrations.length})
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="conditions" pt="md">
        {conditions.length === 0 ? (
          <Text size="sm" c="dimmed">No conditions recorded.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Condition</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Verification</Table.Th>
                <Table.Th>Onset</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {conditions.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{c.display}</Text>
                    {c.code && <Text size="xs" c="dimmed">{c.code}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={CLINICAL_STATUS_COLOR[c.clinicalStatus] ?? 'gray'} variant="light">
                      {c.clinicalStatus}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{c.verificationStatus}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{c.onsetDate ?? '—'}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="medications" pt="md">
        {medications.length === 0 ? (
          <Text size="sm" c="dimmed">No medications recorded.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Medication</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Dosage</Table.Th>
                <Table.Th>Frequency</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {medications.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td><Text size="sm" fw={500}>{m.display}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={MED_STATUS_COLOR[m.status] ?? 'gray'} variant="light">
                      {m.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{m.dosage ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="xs">{m.frequency ?? '—'}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="observations" pt="md">
        {observations.length === 0 ? (
          <Text size="sm" c="dimmed">No observations recorded.</Text>
        ) : (
          <Stack gap="md">
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Observation</Table.Th>
                  <Table.Th>Value</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {observations.map((o) => (
                  <Table.Tr key={o.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{o.display}</Text>
                      {o.code && <Text size="xs" c="dimmed">{o.code}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Text size="sm" fw={500}>
                          {o.value ?? '—'}{o.unit ? ` ${o.unit}` : ''}
                        </Text>
                        {o.interpretation && (
                          <Badge size="xs" color={INTERP_COLOR[o.interpretation] ?? 'gray'} variant="filled">
                            {o.interpretation}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={o.status === 'final' ? 'green' : 'yellow'} variant="light">
                        {o.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{o.effectiveDate ?? '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Vital sign trend charts — one per observation type with numeric values */}
            {observationCharts.size > 0 && (
              <Stack gap="sm">
                <Group gap="xs">
                  <IconChartLine size={16} />
                  <Text size="sm" fw={600}>Trends</Text>
                </Group>
                {Array.from(observationCharts.entries()).map(([obsType, dataPoints]) => (
                  <ObservationChart
                    key={obsType}
                    observationType={obsType}
                    data={dataPoints}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="mar" pt="md">
        {medicationAdministrations.length === 0 ? (
          <Text size="sm" c="dimmed">No medication administration records.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Medication</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Dosage</Table.Th>
                <Table.Th>Given By</Table.Th>
                <Table.Th>Date/Time</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {medicationAdministrations.map((ma) => (
                <Table.Tr key={ma.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{ma.medicationDisplay}</Text>
                    {ma.route && <Text size="xs" c="dimmed">{ma.route}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={MAR_STATUS_COLOR[ma.status] ?? 'gray'} variant="light">
                      {ma.status}
                    </Badge>
                    {ma.wasNotGiven && ma.reasonNotGiven && (
                      <Text size="xs" c="red">{ma.reasonNotGiven}</Text>
                    )}
                  </Table.Td>
                  <Table.Td><Text size="xs">{ma.dosage ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="xs">{ma.performerName ?? '—'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {ma.effectiveDateTime
                        ? new Date(ma.effectiveDateTime).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
